import React from "react";
import PropTypes from "prop-types";
// @mui
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
// components
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  defaultSiteFormValues,
  standortFormData,
} from "../../../components/form/fields/inputFields";
import Iconify from "../../../components/iconify";
import TextField from "../../../components/form/fields/TextField";
import SelectField from "../../../components/form/fields/SelectField";
import {
  deletePublicRegistrationFile,
  uploadFile,
} from "../../../app/api/axios/services/files.service";
import { serverURL } from "../../../app/api/axios/http.service";
import {
  useAddNewSiteMutation,
  useGetSitesQuery,
  useUpdateSiteMutation,
} from "../../../features/sites/sitesApiSlice";
import { useDispatch } from "react-redux";
import Loading from "../../../components/spinners/Loading";
import ErrorAlert from "../../../components/alerts/ErrorAlert";
import { setDialog } from "../../../features/dialog/dialogSlice";
import { Icon } from "@iconify/react";
import {
  openingHoursDayOptions,
  normalizeDayValues,
} from "../../../utils/openingHours";
import usePlanGate from "../../../hooks/usePlanGate";
import { FEATURE_KEYS } from "../../../utils/planFeatures";
import FeatureLockedDialog from "../../../components/dialog/FeatureLockedDialog";

// ----------------------------------------------------------------------

export default function SitesForm({ siteData, dialogType }) {
  const {
    isLoading: isPlanLoading,
    isAllowed: canUsePublicRegistration,
    isDialogOpen: isFeatureDialogOpen,
    requiredPlanLabel,
    openDialog: openFeatureDialog,
    closeDialog: closeFeatureDialog,
    goToUpgrade: goToFeatureUpgrade,
  } = usePlanGate(FEATURE_KEYS.PUBLIC_REGISTRATION);

  const normalizePublicRegistrationDiscounts = (discounts) => {
    if (!Array.isArray(discounts)) return [];
    return discounts.slice(0, 3).map((entry) => {
      const code = entry?.code || "";
      const rawApplyTo = String(entry?.applyTo || "").toLowerCase();
      const applyTo =
        rawApplyTo === "enrollmentfee" || rawApplyTo === "registrationfee"
          ? "enrollmentFee"
          : "contribution";
      const rawType = String(entry?.discountType || "").toLowerCase();
      let discountType = rawType;
      let discountValue = entry?.discountValue;

      if (discountType !== "percent" && discountType !== "amount") {
        if (Number.isFinite(Number(entry?.discountPercent))) {
          discountType = "percent";
          discountValue = entry?.discountPercent;
        } else if (Number.isFinite(Number(entry?.discountAmount))) {
          discountType = "amount";
          discountValue = entry?.discountAmount;
        } else {
          discountType = "percent";
        }
      }

      if (
        (discountValue === undefined ||
          discountValue === null ||
          discountValue === "") &&
        discountType === "percent" &&
        Number.isFinite(Number(entry?.discountPercent))
      ) {
        discountValue = entry.discountPercent;
      }

      if (
        (discountValue === undefined ||
          discountValue === null ||
          discountValue === "") &&
        discountType === "amount" &&
        Number.isFinite(Number(entry?.discountAmount))
      ) {
        discountValue = entry.discountAmount;
      }

      return {
        code,
        applyTo,
        discountType: discountType || "percent",
        discountValue: discountValue ?? "",
      };
    });
  };

  const normalizeTrainingSessionsForForm = (sessions = []) =>
    (Array.isArray(sessions) ? sessions : []).map((session) => ({
      ...session,
      days: normalizeDayValues(session?.days ?? session?.day),
    }));

  const { handleSubmit, control, setError, setValue } = useForm({
    defaultValues: {
      ...defaultSiteFormValues,
      ...siteData,
      email: siteData?.email || siteData?.mail || "",
      mail: siteData?.mail || siteData?.email || "",
      publicRegistrationOptions: siteData?.publicRegistrationOptions || [],
      publicRegistrationDocuments: siteData?.publicRegistrationDocuments || [],
      publicRegistrationDiscounts: normalizePublicRegistrationDiscounts(
        siteData?.publicRegistrationDiscounts || [],
      ),
      trainingSessions: normalizeTrainingSessionsForForm(
        siteData?.trainingSessions || [],
      ),
      openingHours: siteData?.openingHours || [],
      visibleForMembers: siteData?.visibleForMembers ?? true,
      publicInfoText: siteData?.publicInfoText || "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "publicRegistrationOptions",
  });

  const {
    fields: documentFields,
    append: appendDocument,
    remove: removeDocument,
  } = useFieldArray({
    control,
    name: "publicRegistrationDocuments",
  });

  const {
    fields: discountFields,
    append: appendDiscount,
    remove: removeDiscount,
  } = useFieldArray({
    control,
    name: "publicRegistrationDiscounts",
  });

  const {
    fields: openingHoursFields,
    append: appendOpeningHour,
    remove: removeOpeningHour,
  } = useFieldArray({
    control,
    name: "openingHours",
  });

  const maxOpeningHoursEntries = openingHoursDayOptions.length;
  const maxTrainingEntries = 20;

  const {
    fields: trainingFields,
    append: appendTraining,
    remove: removeTraining,
  } = useFieldArray({
    control,
    name: "trainingSessions",
  });

  const { refetch } = useGetSitesQuery();

  const [addNewSite, { isLoading, isError }] = useAddNewSiteMutation();

  const [updateSite, { isLoading: isEditLoading, isError: isEditError }] =
    useUpdateSiteMutation();

  const dispatch = useDispatch();
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [publicRegistrationExpanded, setPublicRegistrationExpanded] =
    React.useState(canUsePublicRegistration);

  const previewDocuments =
    useWatch({ control, name: "publicRegistrationDocuments" }) || [];
  const previewDiscounts =
    useWatch({ control, name: "publicRegistrationDiscounts" }) || [];
  const visibleForMembers =
    useWatch({ control, name: "visibleForMembers" }) ?? true;

  React.useEffect(() => {
    if (canUsePublicRegistration) {
      setPublicRegistrationExpanded(true);
    }
  }, [canUsePublicRegistration]);

  const handlePublicRegistrationAccordionChange = (_, expanded) => {
    if (isPlanLoading) return;
    if (!canUsePublicRegistration) {
      openFeatureDialog();
      return;
    }
    setPublicRegistrationExpanded(expanded);
  };

  const discountTypeOptions = [
    { label: "Prozent", value: "percent" },
    { label: "Fester Betrag", value: "amount" },
  ];
  const discountApplyToOptions = [
    { label: "Monatsbeitrag", value: "contribution" },
    { label: "Anmeldegebühr", value: "enrollmentFee" },
  ];
  const memberVisibilityDetails = [
    {
      icon: "mdi:map-marker-outline",
      label: "Standortname sowie Anschrift (Straße, PLZ, Ort)",
    },
    {
      icon: "mdi:email-outline",
      label:
        "Kontaktangaben (E-Mail, Rufnummer, Homepage), sofern hinterlegt",
    },
    {
      icon: "mdi:clock-time-four-outline",
      label: "Öffnungszeiten (oder alternativ öffentliche Öffnungszeiten)",
    },
    {
      icon: "mdi:calendar-clock-outline",
      label:
        "Trainingsplan (oder alternativ öffentliche Trainingsangaben)",
    },
    {
      icon: "mdi:information-outline",
      label: 'Der Text aus dem Feld "Über den Standort"',
    },
    {
      icon: "mdi:account-plus-outline",
      label: "Optional: Link zur öffentlichen Anmeldung (tarifabhängig)",
    },
  ];

  const getPreviewLabel = (doc, index) =>
    doc?.title || doc?.fileName || `Dokument ${index + 1}`;

  const getPreviewUrl = (doc) => {
    if (!doc) return "";
    if (doc.url) return doc.url;
    if (doc.storageKey) {
      return `${serverURL()}/public/files/${doc.storageKey}`;
    }
    return "";
  };

  const computeSha256 = async (file) => {
    if (!file?.arrayBuffer || !window?.crypto?.subtle) return "";
    const buffer = await file.arrayBuffer();
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const handleDocumentUpload = async (index, file) => {
    if (!file || !siteData?._id) return;
    const folder = `public-registration/${siteData._id}`;
    const fileName = `${Date.now()}_${file.name}`;
    const formData = new FormData();
    formData.append("folder", folder);
    formData.append("file", file, fileName);

    await uploadFile(formData);
    const hash = await computeSha256(file);

    setValue(
      `publicRegistrationDocuments.${index}.storageKey`,
      `${folder}/${fileName}`,
      { shouldDirty: true },
    );
    setValue(`publicRegistrationDocuments.${index}.fileName`, file.name, {
      shouldDirty: true,
    });
    setValue(`publicRegistrationDocuments.${index}.sha256Hash`, hash, {
      shouldDirty: true,
    });
    setValue(
      `publicRegistrationDocuments.${index}.updatedAt`,
      new Date().toISOString(),
      { shouldDirty: true },
    );
  };

  const handleRemoveDocument = async (index) => {
    const doc = previewDocuments?.[index];
    if (doc?.storageKey) {
      try {
        await deletePublicRegistrationFile(doc.storageKey);
      } catch (error) {
        console.error("Failed to delete public registration file", error);
      }
    }
    removeDocument(index);
  };

  const normalizeSitePayload = (payload) => {
    const normalizedMail = String(payload?.mail || payload?.email || "").trim();
    const normalizedPayload = {
      ...payload,
      mail: normalizedMail,
    };
    delete normalizedPayload.email;
    return normalizedPayload;
  };

  const onSubmit = async (data) => {
    const normalizedData = normalizeSitePayload(data);

    if (dialogType === "CREATE") {
      let result = await addNewSite({ ...normalizedData });
      if (result?.error?.status === 409) {
        // Sitename already in use
        setError("siteName", {
          type: "custom",
          message: "Dieser Standortname ist bereits vergeben.",
        });
      }
      if (result?.data?.message === "New site created") {
        refetch();
        dispatch(
          setDialog({
            title: "Neuer Standort wurde erfolgreich erstellt!",
            type: "SUCCESS",
            data: { successText: "Neuer Standort wurde erfolgreich erstellt!" },
          }),
        );
      }
    }

    if (dialogType === "EDIT") {
      let updateSiteData = { ...normalizedData, id: siteData._id };
      delete updateSiteData?._id;
      let result = await updateSite(updateSiteData);
      if (result?.error?.status === 409) {
        // Sitename already in use
        setError("siteName", {
          type: "custom",
          message: "Dieser Standortname ist bereits vergeben.",
        });
      }
      if (result?.data?.type === "success") {
        refetch();
        dispatch(
          setDialog({
            title: data.siteName + " wurde erfolgreich geändert!",
            type: "SUCCESS",
            data: {
              successText: data.siteName + " wurde erfolgreich geändert!",
            },
          }),
        );
      }
    }
  };

  const renderSiteInputFields = (fields) => {
    return fields.map((field) => (
      <Grid item xs={12} md={4}>
        <TextField
          control={control}
          name={field.value}
          label={field.label}
          type={field.type}
          variant="outlined"
          required={field.required}
          errorMsg={field.errorMsg}
          helperText={field.helperText}
          fullWidth
        />
      </Grid>
    ));
  };

  if (isLoading || isEditLoading) {
    return (
      <div style={{ minHeight: 300 }}>
        <Loading />
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ paddingTop: 5 }}
        >
          {isError && (
            <Grid item xs={12}>
              <ErrorAlert errorMessage="Fehler beim erstellen des Standorts" />{" "}
            </Grid>
          )}
          {isEditError && (
            <Grid item xs={12}>
              <ErrorAlert errorMessage="Fehler beim updaten des Standorts" />{" "}
            </Grid>
          )}
          {/* {renderSiteInputFields(standortFormData)} */}
          {renderSiteInputFields(standortFormData.slice(0, 3))}
          <Grid item xs={12}>
            <Typography variant="h6">Vertragsdaten</Typography>
            <Typography variant="body2">
              Diese Daten werden beim Hinzufügen eines neuen Mitglieds in das
              Formular als Standardwerte eingetragen.
            </Typography>
          </Grid>
          {renderSiteInputFields(standortFormData.slice(3, 8))}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(255, 255, 255, 0.95))",
              }}
            >
              <Stack spacing={1.75}>
                <Typography variant="h6">Mitgliederbereich</Typography>
                <Typography variant="body2" color="text.secondary">
                  Steuern Sie, ob dieser Standort im internen
                  Mitgliederbereich unter „Standorte“ angezeigt wird.
                </Typography>
                <FormControlLabel
                  label={
                    visibleForMembers
                      ? "Für Mitglieder sichtbar"
                      : "Für Mitglieder ausgeblendet"
                  }
                  control={
                    <Controller
                      control={control}
                      name="visibleForMembers"
                      render={({ field }) => (
                        <Switch
                          checked={Boolean(field.value)}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      )}
                    />
                  }
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: -0.5 }}
                >
                  {visibleForMembers
                    ? "Der Standort erscheint in der allgemeinen Standortliste für Mitglieder."
                    : "Der Standort wird in der allgemeinen Standortliste ausgeblendet. Zugewiesene Mitglieder sehen ihren eigenen Standort weiterhin."}
                </Typography>
                <TextField
                  control={control}
                  name="publicInfoText"
                  label="Über den Standort"
                  multiline
                  fullWidth
                  helperText="Dieser Text wird Mitgliedern in der Standort-Detailansicht angezeigt."
                />
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "rgba(25, 118, 210, 0.28)",
                    background:
                      "linear-gradient(135deg, rgba(25, 118, 210, 0.06), rgba(255, 255, 255, 0.96))",
                  }}
                >
                  <Stack spacing={1.2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(25, 118, 210, 0.16)",
                          color: "primary.main",
                        }}
                      >
                        <Icon icon="mdi:eye-outline" width={15} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Was Mitglieder in der Standortansicht sehen
                      </Typography>
                    </Stack>

                    <Grid container spacing={0.75}>
                      {memberVisibilityDetails.map((entry) => (
                        <Grid item xs={12} md={6} key={entry.label}>
                          <Stack
                            direction="row"
                            spacing={0.85}
                            alignItems="flex-start"
                          >
                            <Icon
                              icon={entry.icon}
                              width={16}
                              style={{ marginTop: 2, color: "currentColor" }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {entry.label}
                            </Typography>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>

                    <Box
                      sx={{
                        p: 1.1,
                        borderRadius: 1.5,
                        border: "1px dashed",
                        borderColor: "rgba(211, 47, 47, 0.35)",
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        Nicht sichtbar für Mitglieder:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mitgliederanzahl, Mitgliedsbeitrag,
                        Aufnahme-/Vereinsgebühr, Vertragsdaten sowie interne
                        Leitungs- und Trainerinformationen.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Accordion
              expanded={publicRegistrationExpanded}
              onChange={handlePublicRegistrationAccordionChange}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(135deg, rgba(20, 184, 166, 0.12), rgba(255, 255, 255, 0.9))",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon icon="mdi:chevron-down" />}
                sx={{ px: 2, py: 1.5 }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(20, 184, 166, 0.2)",
                      color: "primary.main",
                    }}
                  >
                    <Icon icon="mdi:clock-outline" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1">Öffnungszeiten</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Wochentag, Öffnungszeit und Schließzeit im 24-h-Format
                      hinterlegen.
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "rgba(255, 255, 255, 0.75)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Fügen Sie einzelne Tage hinzu, um für Besucher klar zu
                        kommunizieren, wann der Standort geöffnet ist.
                      </Typography>
                    </Grid>
                    {openingHoursFields.map((field, index) => (
                      <React.Fragment key={field.id}>
                        <Grid item xs={12} md={3}>
                          <SelectField
                            control={control}
                            name={`openingHours.${index}.day`}
                            label="Wochentag"
                            itemArray={openingHoursDayOptions}
                            fullWidth
                            required
                            errorMsg="Bitte einen Wochentag wählen."
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField
                            control={control}
                            name={`openingHours.${index}.start`}
                            label="Öffnet"
                            type="time"
                            helperText="24-h Format"
                            errorMsg="Bitte Öffnungszeit eintragen."
                            required
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField
                            control={control}
                            name={`openingHours.${index}.end`}
                            label="Schließt"
                            type="time"
                            helperText="24-h Format"
                            errorMsg="Bitte Schließzeit eintragen."
                            required
                            fullWidth
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={3}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => removeOpeningHour(index)}
                            fullWidth
                          >
                            Entfernen
                          </Button>
                        </Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          appendOpeningHour({ day: "", start: "", end: "" })
                        }
                        disabled={
                          openingHoursFields.length >= maxOpeningHoursEntries
                        }
                      >
                        Öffnungszeit hinzufügen
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <Accordion
              defaultExpanded
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(135deg, rgba(234, 179, 8, 0.12), rgba(255, 255, 255, 0.9))",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon icon="mdi:chevron-down" />}
                sx={{ px: 2, py: 1.5 }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(234, 179, 8, 0.2)",
                      color: "primary.main",
                    }}
                  >
                    <Icon icon="mdi:account-group-outline" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1">Trainingsplan</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Legen Sie pro Trainingsgruppe Tag, Zeit und Titel fest.
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "rgba(255, 255, 255, 0.75)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Für jeden Eintrag geben Sie Tag, Titel und Uhrzeiten an.
                      </Typography>
                    </Grid>
                    {trainingFields.map((field, index) => (
                      <React.Fragment key={field.id}>
                        <Grid item xs={12} md={3}>
                          <SelectField
                            control={control}
                            name={`trainingSessions.${index}.days`}
                            label="Wochentage"
                            itemArray={openingHoursDayOptions}
                            fullWidth
                            required
                            multiple
                            errorMsg="Bitte mindestens einen Wochentag wählen."
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            control={control}
                            name={`trainingSessions.${index}.title`}
                            label="Trainingsbezeichnung"
                            variant="outlined"
                            fullWidth
                            required
                            errorMsg="Bitte einen Titel eingeben."
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            control={control}
                            name={`trainingSessions.${index}.start`}
                            label="Start"
                            type="time"
                            helperText="24-h Format"
                            errorMsg="Bitte eine Startzeit eintragen."
                            required
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            control={control}
                            name={`trainingSessions.${index}.end`}
                            label="Ende"
                            type="time"
                            helperText="24-h Format"
                            errorMsg="Bitte eine Endzeit eintragen."
                            required
                            fullWidth
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={2}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => removeTraining(index)}
                            fullWidth
                          >
                            Entfernen
                          </Button>
                        </Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          appendTraining({
                            days: [],
                            title: "",
                            start: "",
                            end: "",
                          })
                        }
                        disabled={trainingFields.length >= maxTrainingEntries}
                      >
                        Training hinzufügen
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <Accordion
              defaultExpanded
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(135deg, rgba(33, 150, 243, 0.08), rgba(255, 255, 255, 0.9))",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon icon="mdi:chevron-down" />}
                sx={{ px: 2, py: 1.5 }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(33, 150, 243, 0.12)",
                      color: "primary.main",
                    }}
                  >
                    <Icon icon="mdi:account-box-outline" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1">
                      Öffentliche Anmeldung
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isPlanLoading
                        ? "Tarif wird geladen ..."
                        : canUsePublicRegistration
                        ? "Beitrag, Rabattcodes und Einwilligungen konfigurieren"
                        : "In Ihrem Tarif derzeit nicht enthalten"}
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {isPlanLoading ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Tarifinformationen werden geladen ...
                    </Typography>
                  </Paper>
                ) : !canUsePublicRegistration ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Öffentliche Anmeldung ist erst ab dem Premium-Tarif
                      verfügbar.
                    </Alert>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Klicken Sie auf &quot;Jetzt upgraden&quot;, um den Tarif
                      mit den passenden Funktionen zu aktivieren.
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={goToFeatureUpgrade}
                    >
                      Jetzt upgraden
                    </Button>
                  </Paper>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(255, 255, 255, 0.75)",
                    }}
                  >
                    <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">
                        Beitrag & Mindestvertragslaufzeit
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Legen Sie bis zu drei
                        Beitrag/Mindestvertragslaufzeit-Kombinationen für die
                        öffentliche Anmeldung fest.
                      </Typography>
                    </Grid>
                    {fields.map((field, index) => (
                      <React.Fragment key={field.id}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            control={control}
                            name={`publicRegistrationOptions.${index}.publicContribution`}
                            label="Beitrag in Euro"
                            type="number"
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            control={control}
                            name={`publicRegistrationOptions.${index}.publicContractDuration`}
                            label="Vertragslaufzeit in Monaten"
                            type="number"
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          display="flex"
                          alignItems="center"
                        >
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => remove(index)}
                          >
                            Entfernen
                          </Button>
                        </Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          append({
                            publicContribution: "",
                            publicContractDuration: "",
                          })
                        }
                        disabled={fields.length >= 3}
                      >
                        Kombination hinzufügen
                      </Button>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Typography variant="subtitle1">Rabattcodes</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Bis zu drei Codes mit prozentualem oder festem Rabatt.
                      </Typography>
                    </Grid>
                    {discountFields.map((field, index) => {
                      const currentType =
                        previewDiscounts?.[index]?.discountType || "percent";
                      const isAmount = currentType === "amount";
                      return (
                        <React.Fragment key={field.id}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              control={control}
                              name={`publicRegistrationDiscounts.${index}.code`}
                              label="Rabattcode"
                              variant="outlined"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <SelectField
                              style={{ minWidth: 160 }}
                              control={control}
                              name={`publicRegistrationDiscounts.${index}.applyTo`}
                              label="Gilt für"
                              variant="outlined"
                              itemArray={discountApplyToOptions}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <SelectField
                              style={{ minWidth: 160 }}
                              control={control}
                              name={`publicRegistrationDiscounts.${index}.discountType`}
                              label="Rabattart"
                              variant="outlined"
                              itemArray={discountTypeOptions}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              control={control}
                              name={`publicRegistrationDiscounts.${index}.discountValue`}
                              label={isAmount ? "Rabattbetrag" : "Rabatt in %"}
                              type="number"
                              variant="outlined"
                              fullWidth
                              helperText={
                                isAmount ? "Betrag in EUR" : "Maximal 100%"
                              }
                              endAdornment={isAmount ? "€" : "%"}
                            />
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={2}
                            display="flex"
                            alignItems="center"
                          >
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => removeDiscount(index)}
                              fullWidth
                            >
                              Entfernen
                            </Button>
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          appendDiscount({
                            code: "",
                            applyTo: "contribution",
                            discountType: "percent",
                            discountValue: "",
                          })
                        }
                        disabled={discountFields.length >= 3}
                      >
                        Rabattcode hinzufügen
                      </Button>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="subtitle1">
                          Öffentliche Dokumente & Einwilligungen
                        </Typography>
                        <Tooltip
                          title="Hier können Dokumente hochgeladen werden, die wichtige Informationen zur Anmeldung enthalten. Zum Beispiel Datenschutzerklärung, Gebührenübersicht, Vertragsbedingungen etc."
                          arrow
                          placement="right"
                        >
                          <Iconify
                            icon="clarity:help-solid"
                            sx={{
                              fontSize: 20,
                              color: "action.active",
                              transition: "transform 0.2s",
                              "&:hover": { transform: "scale(1.15)" },
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="body2">
                        Laden Sie bis zu vier PDF-Dokumente hoch und definieren
                        Sie den jeweiligen Einwilligungstext.
                      </Typography>
                      {!siteData?._id && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mt: 1 }}
                        >
                          Speichern Sie den Standort zuerst, um Dokumente
                          hochzuladen.
                        </Typography>
                      )}
                      {dialogType === "EDIT" && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setPreviewOpen(true)}
                          >
                            Vorschau Einwilligungen
                          </Button>
                        </Box>
                      )}
                    </Grid>
                    {documentFields.map((field, index) => (
                      <React.Fragment key={field.id}>
                        <Grid item xs={12}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                              <TextField
                                control={control}
                                name={`publicRegistrationDocuments.${index}.title`}
                                label="Dokumenttitel (Button)"
                                variant="outlined"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                control={control}
                                name={`publicRegistrationDocuments.${index}.consentText`}
                                label="Einwilligungstext (Checkbox)"
                                variant="outlined"
                                fullWidth
                                multiline
                                required
                              />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={2}
                              display="flex"
                              alignItems="center"
                            >
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleRemoveDocument(index)}
                                fullWidth
                              >
                                Entfernen
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                disabled={!siteData?._id}
                              >
                                PDF hochladen
                                <input
                                  type="file"
                                  accept="application/pdf"
                                  hidden
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                      handleDocumentUpload(index, file);
                                      event.target.value = "";
                                    }
                                  }}
                                />
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", px: 1 }}
                              >
                                Dateiname
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ px: 1, pb: 1.5 }}
                              >
                                {field?.fileName || "Kein Dokument hochgeladen"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          appendDocument({
                            title: "",
                            consentText: "",
                            storageKey: "",
                            fileName: "",
                            sha256Hash: "",
                            updatedAt: new Date().toISOString(),
                          })
                        }
                        disabled={documentFields.length >= 4}
                      >
                        Dokument hinzufügen
                      </Button>
                    </Grid>
                    </Grid>
                  </Paper>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Personal</Typography>
          </Grid>
          {renderSiteInputFields(standortFormData.slice(8, 11))}
          <Grid item xs={12}>
            <Typography variant="h6">Anschrift & Kontakt</Typography>
          </Grid>
          {renderSiteInputFields(standortFormData.slice(11, 16))}
        </Grid>
        <Box
          sx={{
            position: "sticky",
            bottom: -20,
            backgroundColor: "background.paper",
            pt: 2,
            pb: 3,
            zIndex: 1,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            sx={{ fontSize: "125%" }}
          >
            {dialogType === "CREATE" ? "Erstellen" : "Speichern"}
          </Button>
        </Box>
      </form>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Vorschau: Dokumente & Einwilligungen</DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Dokumente
          </Typography>
          {previewDocuments.length === 0 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Keine Dokumente vorhanden.
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {previewDocuments.map((doc, index) => {
              const url = getPreviewUrl(doc);
              return (
                <Grid item xs={12} md={6} key={doc?.docId || index}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    component={url ? Link : "button"}
                    href={url || undefined}
                    target={url ? "_blank" : undefined}
                    rel={url ? "noopener noreferrer" : undefined}
                    disabled={!url}
                  >
                    {getPreviewLabel(doc, index)}
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Einwilligungen
          </Typography>
          {previewDocuments.length === 0 && (
            <Typography variant="body2">
              Keine Einwilligungen erforderlich.
            </Typography>
          )}
          {previewDocuments.map((doc, index) => (
            <FormControlLabel
              key={doc?.docId || `consent-${index}`}
              control={<Checkbox checked={false} disabled />}
              label={doc?.consentText || "Ich stimme dem Dokument zu."}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
      <FeatureLockedDialog
        open={isFeatureDialogOpen}
        onClose={closeFeatureDialog}
        onUpgrade={goToFeatureUpgrade}
        upgradePlanLabel={requiredPlanLabel}
      />
    </>
  );
}

SitesForm.propTypes = {
  siteData: PropTypes.object,
  dialogType: PropTypes.string,
};

SitesForm.defaultProps = {
  siteData: defaultSiteFormValues, // Hier den Standardwert festlegen
};
